import type {
  ChannelVisibility,
  ChatPerson,
  ConversationKind,
  LinkedProject,
  ThreadSummary,
  WholeChannelMentions,
} from "@beacon/core/chat"
import { describeRelativeTime } from "@/time/describe.ts"
import { maxDmOthers } from "@beacon/core/chat/rules"
import {
  channelName,
  maxBookmarks,
  maxBookmarkTitle,
  maxChannelDescription,
  maxChannelName,
  maxPins,
  maxStatusText,
} from "@beacon/core/chat/rules"
import { authorWords } from "./authors.ts"

/*
 * What Chat's pages say, kept apart from them so it can be checked without a browser.
 */

/** A refusal from the web API, as a sentence a Member can act on. */
export function describeChatRefusal(refusal: string) {
  switch (refusal) {
    case "name-taken":
      return "A channel already has that name."
    case "invalid-name":
      return `Use letters, numbers, hyphens and underscores, up to ${maxChannelName} characters.`
    case "not-a-participant":
      return "Join the channel to send messages in it."
    case "not-found":
      return "That conversation doesn't exist, or you can't see it."
    case "invalid-message":
      return "That message is empty, or too long to send."
    case "not-the-author":
      return "Only the person who wrote a message can change it."
    case "message-changed":
      return "That message changed while you were editing. Check what it says now, then save again."
    case "not-a-manager":
      return "Only the channel's managers can do that."
    case "last-manager":
      return "You're its only manager. Make someone else a manager, or archive it, first."
    case "archived":
      return "This channel is archived. Unarchive it to send, edit, react, add people or change its details."
    case "invalid-reaction":
      return "React with one emoji: a standard one, or a custom one that hasn't been removed."
    case "too-many-reactions":
      return "That message has as many different reactions as it can take. Add yours to one of them."
    case "not-claimable":
      return "Someone already manages this channel."
    case "invalid-participant":
      return "Only active Members can be in a conversation."
    case "not-leavable":
      return "A direct message stays with the two of you; it can't be left."
    case "not-joinable":
      return "A private channel is joined by being added to it."
    case "invalid-status":
      return `A status needs an emoji or words (up to ${maxStatusText} characters), and a time to clear it within the next year.`
    case "invalid-description":
      return `A description can be up to ${maxChannelDescription} characters.`
    case "invalid-project":
      return "That Project can't be linked. Link an open Project you can see, other than your Personal Project."
    case "too-many-pins":
      return `A conversation keeps up to ${maxPins} pinned messages. Unpin one first.`
    case "too-many-bookmarks":
      return `A channel keeps up to ${maxBookmarks} bookmarks. Remove one first.`
    case "channel-mentions-not-allowed":
      return "Only this channel's managers can use @channel and @here. Take them out to send it."
    case "invalid-attention":
      return "That can't be saved. Quiet hours need different start and end times, and a pause ends within the year."
    case "invalid-bookmark":
      return `Bookmark a web link, starting https://, with a title of up to ${maxBookmarkTitle} characters.`
    case "too-large":
      return "Files can be up to 100 MB."
    case "not-uploaded":
      return "The file didn't arrive whole. Try it again."
    case "invalid-file":
      return "A file can't be sent from here. Remove it and attach it again."
    case "reply-current":
      return "Beacon's answer is being written, or still stands. Ask again once it's withheld."
    case "reply-done":
      return "Beacon had already finished answering."
    default:
      return "Something went wrong. Try again."
  }
}

/** The refusals about a channel's name, which are said under the name. */
export const nameRefusals = new Set(["name-taken", "invalid-name"])

/**
 * What a typed name becomes, said under the field as the Member types: the channel's name with its
 * `#` once it differs from what they typed, or nothing.
 */
export function namePreview(typed: string) {
  if (typed.trim() === "") return undefined
  const name = channelName(typed)
  if (!name) return undefined
  return name === typed ? undefined : `Will be #${name}`
}

/**
 * What is wrong with a typed name, said under the field as the Member types, by the rule the web
 * API holds it to: nothing while it is empty or makes a channel's name.
 */
export const nameProblem = (typed: string) =>
  typed.trim() === "" || channelName(typed) ? undefined : describeChatRefusal("invalid-name")

/** How many have joined a channel, as its row says it. */
export const describeParticipants = (count: number) =>
  count === 1 ? "1 member" : `${count} members`

/**
 * The formats a conversation's rows say their times and days in, made once for each zone: making
 * one costs far more than using it, and a list says every loaded message's at each word of a reply.
 */
const formatsIn = new Map<
  string,
  { time: Intl.DateTimeFormat; day: Intl.DateTimeFormat; named: Intl.DateTimeFormat }
>()
function formatsOf(zone: string) {
  let formats = formatsIn.get(zone)
  if (!formats) {
    formats = {
      time: new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: zone,
      }),
      day: new Intl.DateTimeFormat("en-CA", { timeZone: zone, dateStyle: "short" }),
      named: new Intl.DateTimeFormat("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        timeZone: zone,
      }),
    }
    formatsIn.set(zone, formats)
  }
  return formats
}

/** A message's time, as its row shows it: 14:05, in the viewer's own clock. */
export const timeOf = (sentAt: string, zone: string) =>
  formatsOf(zone).time.format(new Date(sentAt))

/** A day's divider in a conversation, in the viewer's zone: Today, Yesterday, Wednesday 7 October. */
export function dayOf(sentAt: string, zone: string, now: Date) {
  const formats = formatsOf(zone)
  const said = formats.day.format(new Date(sentAt))
  if (said === formats.day.format(now)) return "Today"
  if (said === formats.day.format(new Date(now.getTime() - 24 * 60 * 60_000))) return "Yesterday"
  return formats.named.format(new Date(sentAt))
}

/**
 * What an archived channel says where its message box would be: who still reads it, and who may
 * bring it back (the Member, when they manage it).
 */
export function describeArchived(kind: ConversationKind, manages: boolean) {
  const readers =
    kind === "public-channel"
      ? "Everyone at the company can still read it"
      : "Its members can still read it"
  const back = manages ? "Unarchive it to use it again." : "Its managers can unarchive it."
  return `This channel is archived. ${readers}, but nobody can send messages here. ${back}`
}

/** What names a conversation wherever it is shown: a channel by its name, the rest by who. */
type Named = { kind: ConversationKind; name: string | null; with: readonly ChatPerson[] }

/** People named in a run: "Ada", "Ada and Ben", "Ada, Ben and Cy". */
const namesOf = (people: readonly Pick<ChatPerson, "name">[]) =>
  new Intl.ListFormat("en-GB", { type: "conjunction" }).format(people.map(({ name }) => name))

/**
 * A conversation's name, as its header, its rows and its box say it: a channel's after its hash, a
 * direct message by the people it is with ("Only you" once they have all left a group), a Member's
 * Beacon DM as Beacon. A group left with one other person says it is a group, so it isn't taken for
 * a one-to-one DM with them.
 */
export function placeOf({ kind, name, with: people }: Named) {
  if (kind === "public-channel" || kind === "private-channel") return `#${name ?? ""}`
  if (kind === "beacon") return "Beacon"
  if (people.length === 0) return "Only you"
  return kind === "group-dm" && people.length === 1 ? `${namesOf(people)} (group)` : namesOf(people)
}

/** What the top of a conversation says, where its history begins: what it is, and who reads it. */
export function beginningOf(conversation: Named) {
  const place = placeOf(conversation)
  switch (conversation.kind) {
    case "public-channel":
      return `This is the very beginning of ${place}. ${describeVisibility("public")}`
    case "private-channel":
      return `This is the very beginning of ${place}. ${describeVisibility("private")}`
    case "dm":
      return `This is the very beginning of your direct messages with ${place}. Only the two of you can read them.`
    case "group-dm":
      return "This is the very beginning of this group conversation. Only the people in it can read it."
    case "beacon":
      return "This is your private conversation with Beacon. Only you can read it, and everything you write here asks Beacon."
  }
}

/**
 * What New message says at its foot, for the `chosen` people besides the Member: who will read it,
 * or, opened `from` a direct message to change who it is with, that it starts another
 * conversation, which shows none of the first, or, while they are still the `same` people, how to
 * start one; or that too many are chosen.
 */
export function describeDmStart({
  chosen,
  from,
  same = false,
}: {
  chosen: number
  from?: string
  same?: boolean
}) {
  if (chosen > maxDmOthers)
    return `A conversation can be with up to ${maxDmOthers} people besides you.`
  if (!from) return "Only the people in it can read it."
  return same
    ? "Add or remove someone to start a separate conversation."
    : `This starts a separate conversation. None of what was said with ${from} comes with it.`
}

/** Whether the people chosen are exactly the others `from` a direct message is with. */
export function samePeople(chosen: readonly string[], from: { with: readonly { id: string }[] }) {
  const others = new Set(from.with.map(({ id }) => id))
  return chosen.length === others.size && chosen.every((id) => others.has(id))
}

/** Who reads a channel of each visibility, as New channel says it under its choice. */
export const describeVisibility = (visibility: ChannelVisibility) =>
  visibility === "public"
    ? "Everyone at the company can find and read it."
    : "Only the people in it can find or read it, its whole history included."

/**
 * Who a channel's manager may add: every active Member who isn't in it yet, by name. The web API
 * decides again as they are added.
 */
export function addableOf(
  people: readonly { id: string; name: string; deactivatedAt: string | null }[],
  participants: readonly { id: string }[],
) {
  const inIt = new Set(participants.map(({ id }) => id))
  return people
    .filter(({ id, deactivatedAt }) => deactivatedAt === null && !inIt.has(id))
    .map(({ id, name }) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** What a channel's Members row may offer: making them a manager, stopping that, removing them. */
export type ParticipantAction = "appoint" | "dismiss" | "step-down" | "remove"

/**
 * What the Members row of `person` offers the Member `me`: nothing unless they manage the channel;
 * then, for anyone else, making them a manager or stopping them being one, and removing them; for
 * themselves, stepping down (or, an Admin managing a public channel, becoming one of its managers).
 * Leaving is the header's, not the row's. The web API decides again as they choose.
 */
export function participantActionsOf(
  person: { id: string; manager: boolean },
  { me, manages }: { me: string; manages: boolean },
): ParticipantAction[] {
  if (!manages) return []
  if (person.id === me) return [person.manager ? "step-down" : "appoint"]
  return [person.manager ? "dismiss" : "appoint", "remove"]
}

/** How many replies a thread holds: "1 reply", "4 replies". */
export const describeReplies = (count: number) => `${count} ${count === 1 ? "reply" : "replies"}`

/**
 * A thread under the message it started from, as its button tells a screen reader: how many
 * replies, from whom, how long since the last, and that it opens.
 */
export function describeThread(thread: ThreadSummary, now: Date) {
  const from = thread.people.length
    ? ` from ${namesOf(thread.people.map((author) => ({ name: authorWords(author) })))}`
    : ""
  const last = thread.lastReplyAt
    ? `, the last ${describeRelativeTime(thread.lastReplyAt, now)}`
    : ""
  return `${describeReplies(thread.replyCount)}${from}${last}. Open thread`
}

/**
 * The Projects a channel's manager may link it to, by key: the open ones they can see, leaving out
 * their Personal Project, which no channel shows. The web API decides again as they link one.
 */
export function linkableOf(
  projects: readonly { key: string; name: string; personal: boolean; archived: boolean }[],
) {
  return projects
    .filter(({ personal, archived }) => !personal && !archived)
    .map(({ key, name }) => ({ key, name }))
    .sort((a, b) => a.key.localeCompare(b.key))
}

/**
 * A channel's Project, as its details name it to this reader: by its name when they can open it,
 * and as no more than a Project when they can't, so nothing of it shows.
 */
export function describeLinkedProject(project: LinkedProject | null) {
  if (!project) return "No Project"
  if (!project.readable) return "A Project you can't open"
  return project.archived ? `${project.name} (archived)` : project.name
}

/** What linking a Project means, said beside the choice. */
export const projectLinkNote =
  "A linked Project is context only. It gives nobody access to the Project, nor changes who can read this channel; only people who can open the Project see which it is."

/** Who may use `@channel` and `@here` in `channel` ("#general"), as its details say it. */
export const describeWholeChannelMentions = (who: WholeChannelMentions, channel: string) =>
  who === "participants"
    ? `Everyone in ${channel} can use @channel and @here.`
    : "Only its managers can use @channel and @here."

/** Whom `@channel` and `@here` tell, said beside who may use them. */
export const wholeChannelNote = (channel: string) =>
  `@channel tells everyone who joined ${channel}, and @here tells those of them online now. Neither adds anyone to it or shows it to anyone else.`

/** How many characters a typed description has left, as the web API counts them (trimmed). */
export const descriptionLeft = (typed: string) => maxChannelDescription - typed.trim().length
